import { useEffect, useState, type ReactNode } from 'react';
import Layout from '../../components/Layout';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Factory, SquarePen } from 'lucide-react';
import api from '@/services/api';
import { useLanguage } from '@/context/LanguageContext';
import { useThemeMode } from '@/context/ThemeContext';
import Card from '@/components/Card';
import BarChart from '@/components/BarChart';
import LatencyCard from '@/components/LatencyCard';
import EquipmentStats from './EquipmentStats';
import DraggableCard from '../../components/DraggableCard';
import ServerInfo from './ServerInfo';
import { useDragReorder } from '../../hooks/useDragReorder';
import { CardTitle, ContentStack, EditionFab } from './styles';
import EditDashboard from './EditDashboard';

type LatencyFieldErrorsType = Record<
  number,
  { name?: string; address?: string }
>;

// ping address withhout id value
function toEditablePingAddresses(
  pingAddresses: PingAddressType[]
): PingAddressType[] {
  return pingAddresses.map(({ name, address, icon }) => ({
    name,
    address,
    icon,
  }));
}

// user preferences without id on pingaddress
function sanitizePreferences(
  preferences: UserPreferencesType
): UserPreferencesType {
  return {
    ...preferences,
    pingAddresses: toEditablePingAddresses(preferences.pingAddresses),
  };
}

export default function Dashboard() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { setLanguage } = useLanguage();
  const { setMode: setThemeMode } = useThemeMode();

  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [isEditionFabHovered, setIsEditionFabHovered] = useState(false);
  const [draftPreferences, setDraftPreferences] =
    useState<UserPreferencesType | null>(null);
  const [latencyErrors, setLatencyErrors] = useState<LatencyFieldErrorsType>(
    {}
  );
  const [manufacturerDevices, setManufacturerDevices] =
    useState<ManufacturerCountType | null>(null);
  const [manufacturerLoading, setManufacturerLoading] = useState(false);

  // update componentsorder when user reorder cards
  const handleReorder =
    (key: keyof ComponentsOrderType) => (order: string[]) => {
      setDraftPreferences((prev) =>
        prev
          ? {
              ...prev,
              componentsOrder: { ...prev.componentsOrder, [key]: order },
            }
          : prev
      );
    };

  // Each stack is initialised from the backend's componentsOrder
  const networkStack = useDragReorder(
    draftPreferences?.componentsOrder.networkData ?? [],
    handleReorder('networkData')
  );
  const serverStack = useDragReorder(
    draftPreferences?.componentsOrder.serverInfo ?? [],
    handleReorder('serverInfo')
  );
  const deviceStatusStack = useDragReorder(
    draftPreferences?.componentsOrder.deviceStatus ?? [],
    handleReorder('deviceStatus')
  );
  const columnStack = useDragReorder(
    draftPreferences?.componentsOrder.columnsOrder ?? [],
    handleReorder('columnsOrder')
  );

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await fetch('/api/dashboard');
        if (!response.ok) throw new Error('Erro ao buscar dados do dashboard');
        const data: DashboardResponse = await response.json();
        setDashboard(data);
        setDraftPreferences(sanitizePreferences(data.userPreferences));
        syncPreferencesToLocalStorage(data.userPreferences);
      } catch (error) {
        console.error(error);
        enqueueSnackbar(t('dashboard.error') || 'Erro ao carregar dashboard', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    const syncPreferencesToLocalStorage = (
      preferences: UserPreferencesType
    ) => {
      const apiLanguage = preferences.language?.trim().toLowerCase();
      const storedLanguage = localStorage.getItem('app_language');
      if (apiLanguage && storedLanguage !== apiLanguage) {
        setLanguage(apiLanguage);
      }

      const apiTheme = preferences.theme?.trim().toLowerCase();
      const storedTheme = localStorage.getItem('theme');
      if (apiTheme && storedTheme !== apiTheme) {
        setThemeMode(apiTheme as 'light' | 'dark');
      }
    };

    fetchDashboard();
  }, [enqueueSnackbar, t]);

  const handleOpenEditDrawer = () => {
    setEditMode(true);
    setEditDrawerOpen(true);
  };

  const handleCloseEditDrawer = (status?: boolean) => {
    // keep edit mode if user clicks outside the drawer, but close the drawer
    if (status == null) {
      setEditMode(true);
    } else {
      if (status == false) {
        // If the user cancels, reset the draft preferences to the last saved user preferences
        setDraftPreferences(sanitizePreferences(dashboard!.userPreferences));
        const { componentsOrder } = dashboard!.userPreferences;
        networkStack.resetOrder(componentsOrder.networkData);
        serverStack.resetOrder(componentsOrder.serverInfo);
        deviceStatusStack.resetOrder(componentsOrder.deviceStatus);
        columnStack.resetOrder(componentsOrder.columnsOrder);
      } else if (draftPreferences) {
        // If the user saved, promote the draft to the new baseline so a
        // future cancel reverts to this saved state, not the initial fetch
        setDashboard((prev) =>
          prev ? { ...prev, userPreferences: draftPreferences } : prev
        );
      }
      setEditMode(false);
    }
    setLatencyErrors({});
    setEditDrawerOpen(false);
  };

  const handleLatencyAddressesChange = (pingAddresses: PingAddressType[]) => {
    setDraftPreferences((prev) => (prev ? { ...prev, pingAddresses } : prev));
    setLatencyErrors({});
  };

  {
    /* Fetch devices from selected manufacturer */
  }
  const handleManufacturerClick = async (
    item: Record<string, string | number>
  ) => {
    const manufacturer = String(item.manufacturer);
    setManufacturerLoading(true);
    try {
      const { data } = await api.get<ManufacturerCountType>(
        `/dashboard/${manufacturer}`
      );
      setManufacturerDevices(data);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t('dashboard.manufacturerError'), { variant: 'error' });
    } finally {
      setManufacturerLoading(false);
    }
  };

  const handleBackToManufacturers = () => setManufacturerDevices(null);

  const networkCards: Record<string, ReactNode> = {
    deviceStatus: dashboard && (
      <EquipmentStats
        loading={loading}
        userPreferences={draftPreferences!}
        deviceStatus={
          dashboard?.deviceStatus ?? {
            online: 0,
            offline: 0,
            total: 0,
            enabled: 0,
            disabled: 0,
          }
        }
        editMode={editMode}
        dragStack={deviceStatusStack}
      />
    ),
    latency: dashboard && (
      <LatencyCard
        dataLatency={draftPreferences!.pingAddresses}
        editMode={editMode}
        onChangeAddresses={handleLatencyAddressesChange}
        errors={latencyErrors}
      />
    ),
    manufacturers: (
      <Card>
        <CardTitle>
          {manufacturerDevices ? (
            <IconButton
              size="small"
              onClick={handleBackToManufacturers}
              sx={{ padding: 0 }}
            >
              <ArrowLeft size={20} />
            </IconButton>
          ) : (
            <Factory />
          )}
          <Typography variant="h4">
            {manufacturerDevices
              ? manufacturerDevices.manufacturer
              : t('dashboard.manufacturers')}
          </Typography>
          {manufacturerLoading && <CircularProgress size={16} />}
        </CardTitle>
        <BarChart
          data={
            manufacturerDevices
              ? manufacturerDevices.models
              : (dashboard?.manufacturerCount ?? [])
          }
          xAxisKey={manufacturerDevices ? 'modelName' : 'manufacturer'}
          seriesKey="total"
          layout="horizontal"
          height={300}
          onItemClick={
            manufacturerDevices ? undefined : handleManufacturerClick
          }
        />
      </Card>
    ),
  };

  // columnStack.order drives which column renders first
  const columns: Record<string, ReactNode> = {
    networkData: (
      <Stack key="networkData" direction="column" gap="24px">
        {networkStack.order.map(
          (id, index) =>
            draftPreferences![id as keyof UserPreferencesType] !== false && (
              <DraggableCard
                key={id}
                editMode={editMode}
                isDragging={networkStack.draggingIndex === index}
                isDragOver={networkStack.dragOverIndex === index}
                onDragStart={networkStack.handleDragStart(index)}
                onDragOver={networkStack.handleDragOver(index)}
                onDrop={networkStack.handleDrop(index)}
                onDragEnd={networkStack.handleDragEnd}
              >
                {networkCards[id]}
              </DraggableCard>
            )
        )}
      </Stack>
    ),
    serverInfo: dashboard && (
      <ServerInfo
        key="serverInfo"
        dashboard={dashboard}
        editMode={editMode}
        dragStack={serverStack}
        userPreferences={draftPreferences!}
      />
    ),
  };

  return (
    <Layout noLeftMargin>
      {loading ? (
        <Stack height="100%" alignItems="center" direction="row">
          <CircularProgress size={22} color="primary" />
          <Typography
            variant="h5"
            sx={{
              marginLeft: '8px',
              color: (theme) => theme.palette.white['06'],
            }}
          >
            {t('common.loading')}
          </Typography>
        </Stack>
      ) : (
        <>
          <EditionFab
            className="dashboard-edit-trigger"
            expanded={editMode || isEditionFabHovered}
            onClick={handleOpenEditDrawer}
            onMouseEnter={() => setIsEditionFabHovered(true)}
            onMouseLeave={() => setIsEditionFabHovered(false)}
            aria-label={t('dashboard.editLayout')}
          >
            <SquarePen size={20} />
            {editMode
              ? t('dashboard.editModeActive')
              : isEditionFabHovered && t('common.edit')}
          </EditionFab>
          <Drawer
            anchor="right"
            open={editDrawerOpen}
            onClose={() => handleCloseEditDrawer()}
            slotProps={{
              paper: {
                sx: {
                  width: { xs: '100vw', sm: 360 },
                  backgroundColor: (theme) => theme.palette.white['01'],
                },
              },
            }}
          >
            <EditDashboard
              handleSuccess={handleCloseEditDrawer}
              userPreferences={dashboard!.userPreferences}
              draftPreferences={draftPreferences!}
              setDraftPreferences={setDraftPreferences}
              setLatencyErrors={setLatencyErrors}
            />
          </Drawer>

          <ContentStack>
            {columnStack.order.map((colId) => columns[colId])}
          </ContentStack>
        </>
      )}
    </Layout>
  );
}
